import * as React from "react";
import { ChevronRight, ChevronDown, Plus, Upload } from "lucide-react";
import { KnowledgeBaseWithDocuments, KnowledgeBaseDocument, KnowledgeBaseFolder } from "../../../../types/navigationTypes";
import DocumentItem from "../molecules/DocumentItem";
import { ICON_SIZES } from "@/lib/icon-sizes";
import { cn } from "@/lib/utils";
import { KB_ROOT_UPLOAD_FOLDER_KEY } from "../../kbUiConstants";

interface KnowledgeBaseItemProps {
  knowledgeBase: KnowledgeBaseWithDocuments;
  folders: KnowledgeBaseFolder[];
  isExpanded: boolean;
  onToggle: (kbId: string) => void;
  onViewDocument: (doc: KnowledgeBaseDocument) => void;
  onDownloadDocument: (doc: KnowledgeBaseDocument) => void;
  onCreateFolder: (kbId: string) => void;
  onUploadFiles: (kbId: string, folderName: string, files: FileList, isDirect?: boolean) => void;
  isReadOnly?: boolean;
}

/**
 * Determina el icono a mostrar según el tipo de Knowledge Base
 * Nota: Para google-drive, asegúrate de tener assets/icons/drive-icon.png
 */
const getKnowledgeBaseIcon = (type: string | undefined): string => {
  switch (type) {
    case 'sharepoint':
      return 'assets/icons/microsoft-sharepoint-icon.svg';
    case 'google-drive':
      // TODO: Agregar assets/icons/drive-icon.png al proyecto
      return 'assets/icons/file-folder-icon.svg'; // Fallback hasta tener el icono
    case 'outlook':
      return 'assets/icons/microsoft-outlook-icon.svg';
    case 'normal':
    default:
      return 'assets/icons/file-folder-icon.svg';
  }
};

/**
 * Determina el color de fondo del icono según el tipo de KB
 */
const getKnowledgeBaseIconBg = (type: string | undefined): string => {
  switch (type) {
    case 'sharepoint':
      return 'bg-[#0078d4]/10'; // Azul SharePoint
    case 'google-drive':
      return 'bg-[#4285f4]/10'; // Azul Google
    case 'outlook':
      return 'bg-[#0078d4]/10'; // Azul Outlook
    case 'normal':
    default:
      return 'bg-gray-100';
  }
};

const KnowledgeBaseItem: React.FC<KnowledgeBaseItemProps> = ({
  knowledgeBase,
  folders,
  isExpanded,
  onToggle,
  onViewDocument,
  onDownloadDocument,
  onCreateFolder,
  onUploadFiles,
  isReadOnly = false
}) => {
  const [expandedFolders, setExpandedFolders] = React.useState<Set<string>>(new Set());
  const [isDraggingOver, setIsDraggingOver] = React.useState<string | null>(null);
  const fileInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});
  
  // Determinar icono y estilo según el tipo de KB
  const kbIcon = getKnowledgeBaseIcon(knowledgeBase.type);
  const kbIconBg = getKnowledgeBaseIconBg(knowledgeBase.type);
  const kbIconAlt = knowledgeBase.type ? `${knowledgeBase.type} icon` : 'folder icon';

  const dragHighlightId = (target: KnowledgeBaseFolder | null) =>
    target === null ? KB_ROOT_UPLOAD_FOLDER_KEY : target.id;

  const handleDragOver = (e: React.DragEvent, target: KnowledgeBaseFolder | null) => {
    if (isReadOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(dragHighlightId(target));
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(null);
  };

  const handleDrop = (e: React.DragEvent, target: KnowledgeBaseFolder | null) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(null);

    if (isReadOnly) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const uploadKey =
        target === null ? KB_ROOT_UPLOAD_FOLDER_KEY : target.name;
      onUploadFiles(knowledgeBase.id, uploadKey, e.dataTransfer.files, true);
    }
  };

  const folderIds = React.useMemo(() => new Set(folders.map((f) => f.id)), [folders]);

  /** Files at KB root (`parent_id` null) or orphaned parent — top-level rows, not a synthetic "Unfiled" folder. */
  const rootDocuments = knowledgeBase.documents.filter(
    (d) => !d.parent_id || !folderIds.has(d.parent_id)
  );

  /** Documents inside real folders only (matched by folder id). */
  const groupDocumentsByFolder = (
    documents: KnowledgeBaseDocument[],
    allFolders: KnowledgeBaseFolder[]
  ): { folder: KnowledgeBaseFolder; docs: KnowledgeBaseDocument[] }[] => {
    return allFolders.map((folder) => ({
      folder,
      docs: documents.filter((d) => d.parent_id === folder.id),
    }));
  };

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const firstFolderId = folders[0]?.id;

  // Auto-expand first real folder when KB opens (root files stay visible without a folder row)
  React.useEffect(() => {
    if (!isExpanded || !firstFolderId) return;
    setExpandedFolders((prev) => (prev.size > 0 ? prev : new Set([firstFolderId])));
  }, [isExpanded, firstFolderId]);

  const folderGroups = groupDocumentsByFolder(knowledgeBase.documents, folders);

  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden mb-3 hover:shadow-md transition-shadow duration-200">
      {/* KB Header */}
      <div
        onClick={() => onToggle(knowledgeBase.id)}
        className={cn(
          "py-3 px-4 cursor-pointer flex items-center gap-3 transition-colors",
          isExpanded 
            ? "bg-gray-50/50 border-b border-gray-100" 
            : "bg-white hover:bg-gray-50"
        )}
      >
        {isExpanded ? (
          <ChevronDown size={ICON_SIZES.md} className="text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronRight size={ICON_SIZES.md} className="text-slate-400 flex-shrink-0" />
        )}
        <div className={cn("p-1.5 rounded-md", kbIconBg)}>
           <img 
             src={kbIcon} 
             alt={kbIconAlt}
             style={{ width: ICON_SIZES.md, height: ICON_SIZES.md }} 
             className="flex-shrink-0 object-contain" 
           />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="m-0 text-sm font-semibold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap">
            {knowledgeBase.name}
          </h3>
        </div>
        <div className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium flex-shrink-0">
          {knowledgeBase.documents.length} items
        </div>
      </div>

      {/* Documents List - Table View */}
      {isExpanded && (
        <div className="bg-white">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_80px_100px_70px_80px] gap-2 py-2 px-4 bg-white border-b border-gray-100 text-xs font-semibold text-slate-400 uppercase tracking-wide">
            <div>Name</div>
            <div>Size</div>
            <div>Modified</div>
            <div>Status</div>
            <div className="text-right">Actions</div>
          </div>

          {knowledgeBase.documents.length === 0 && folders.length === 0 ? (
            <div className="py-8 px-4 text-center text-muted-foreground flex flex-col items-center gap-3">
              <p className="m-0 text-sm">
                No folders in this Knowledge Base
              </p>
              {!isReadOnly && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateFolder(knowledgeBase.id);
                  }}
                  className="text-[10px] bg-slate-900 text-white px-3 py-1.5 rounded-md hover:bg-slate-800 transition-colors flex items-center gap-1 font-medium"
                >
                  <Plus size={12} />
                  Create Folder
                </button>
              )}
            </div>
          ) : (
            <div>
              {/* Root-level files (parent_id null) — same level as folder rows, not a synthetic folder */}
              {(rootDocuments.length > 0 || !isReadOnly) && (
                <div
                  className={cn(
                    'bg-white border-b border-gray-100',
                    isDraggingOver === KB_ROOT_UPLOAD_FOLDER_KEY &&
                      'bg-blue-50/50 border-2 border-blue-200 border-dashed'
                  )}
                  onDragOver={(e) => handleDragOver(e, null)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, null)}
                >
                  {rootDocuments.map((doc) => (
                    <DocumentItem
                      key={doc.id}
                      document={doc}
                      onView={onViewDocument}
                      onDownload={onDownloadDocument}
                      listIndent="root"
                    />
                  ))}
                  {!isReadOnly && rootDocuments.length === 0 && (
                    <div className="py-4 px-4 pl-4 text-xs text-muted-foreground">
                      Drop files here for knowledge base root or
                      <label className="inline-flex items-center gap-1 ml-1 cursor-pointer text-blue-600 hover:text-blue-700">
                        <Upload size={12} />
                        <span>upload</span>
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          ref={(el) => {
                            fileInputRefs.current[KB_ROOT_UPLOAD_FOLDER_KEY] = el;
                          }}
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              onUploadFiles(
                                knowledgeBase.id,
                                KB_ROOT_UPLOAD_FOLDER_KEY,
                                e.target.files,
                                true
                              );
                            }
                          }}
                        />
                      </label>
                    </div>
                  )}
                </div>
              )}

              {folderGroups.map(({ folder, docs }) => {
                const isFolderExpanded = expandedFolders.has(folder.id);

                return (
                  <div key={folder.id}>
                    {/* Folder Row */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFolder(folder.id);
                      }}
                      className="flex items-center gap-2 py-2 px-4 cursor-pointer bg-white border-b border-gray-100 transition-colors hover:bg-gray-50"
                    >
                      {isFolderExpanded ? (
                        <ChevronDown size={ICON_SIZES.sm} className="text-gray-400" />
                      ) : (
                        <ChevronRight size={ICON_SIZES.sm} className="text-gray-400" />
                      )}
                      <img 
                        src="assets/icons/file-folder-icon.svg" 
                        alt="folder" 
                        style={{ width: ICON_SIZES.sm, height: ICON_SIZES.sm }} 
                        className="flex-shrink-0 object-contain" 
                      />
                      <span className="text-xs font-medium text-gray-700">
                        {folder.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        ({docs.length})
                      </span>
                    </div>

                    {/* Documents in Folder */}
                    {isFolderExpanded && (
                      <div 
                        className={cn(
                          "bg-gray-50/50",
                          isDraggingOver === folder.id && "bg-blue-50/50 border-2 border-blue-200 border-dashed"
                        )}
                        onDragOver={(e) => handleDragOver(e, folder)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, folder)}
                      >
                        {docs.length === 0 ? (
                          <div className="py-4 px-4 pl-12 text-xs text-muted-foreground">
                            {isReadOnly ? (
                              <span className="text-gray-400">Empty folder</span>
                            ) : (
                              <>
                                Drop files here or
                                <label className="inline-flex items-center gap-1 ml-1 cursor-pointer text-blue-600 hover:text-blue-700">
                                  <Upload size={12} />
                                  <span>upload</span>
                                  <input
                                    type="file"
                                    multiple
                                    className="hidden"
                                    ref={(el) => { fileInputRefs.current[folder.id] = el; }}
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files.length > 0) {
                                        onUploadFiles(knowledgeBase.id, folder.name, e.target.files, true);
                                      }
                                    }}
                                  />
                                </label>
                              </>
                            )}
                          </div>
                        ) : (
                          <div>
                            {docs.map((doc) => (
                              <DocumentItem
                                key={doc.id}
                                document={doc}
                                onView={onViewDocument}
                                onDownload={onDownloadDocument}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {/* Create Folder Button - Hidden for read-only KBs */}
              {!isReadOnly && (
                <div className="py-3 px-4 border-t border-gray-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateFolder(knowledgeBase.id);
                    }}
                    className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1.5 transition-colors"
                  >
                    <Plus size={14} />
                    Create Folder
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KnowledgeBaseItem;
